import { Injectable, afterNextRender, effect, inject, signal } from '@angular/core';

import { AppVersionService } from '../app-version/app-version.service';
import { PlatformService } from '../platform/platform.service';

/**
 * Reports this app's readiness and service-worker state to the native desktop shell.
 *
 * ## Why the shell needs to be told
 *
 * The Electron shell remote-loads `https://connect.{domain}` and shows a local splash
 * until the app is usable. From outside the page it cannot tell the difference between
 * "the document's load event fired" and "Angular has bootstrapped and rendered", and it
 * cannot see the service worker pulling a copy of the app into the Cache API at all.
 * Both of those are things this app knows, so this service hands them over: the shell
 * dismisses its splash on real readiness instead of on a timer, and can say
 * "Downloading app data…" only when that is actually true.
 *
 * ## Inert everywhere else
 *
 * On the web — and under Capacitor — `window.crewdleNative` does not exist, so the
 * constructor returns before creating any `effect()` and nothing is ever sent. No
 * `try`/`catch` fallback, no polling, no timers: the whole service is one early return
 * away from doing nothing.
 *
 * ## Forward compatibility
 *
 * The shell and this app ship independently, so an installed shell may predate the
 * `app.reportStatus` channel. The channel was added additively, which by the bridge's
 * versioning rule does NOT bump `crewdleNative.version`, so the version number cannot
 * answer "does this shell know the channel?". Two layers handle it: the optional
 * `channels` advertisement when the shell publishes one, and disabling on the first
 * rejection when it does not.
 */

/** The channel name on the shell's `window.crewdleNative` bridge. */
const CHANNEL = 'app.reportStatus';

/** The snapshot sent to the shell. Mirrors `AppReportStatusPayload` in the shell's src/shared/bridge.ts. */
export interface NativeAppStatus {
  /** Angular has bootstrapped and rendered. The shell's cue to dismiss its splash. */
  interactive: boolean;
  /** An Angular service worker is registered for this page. */
  swEnabled: boolean;
  /** The worker is fetching a version of the app into the Cache API. */
  downloading: boolean;
  /** A newer version is cached and applies on the next navigation. */
  updateReady: boolean;
}

/** The part of the shell's bridge this service touches. */
interface CrewdleNative {
  channels?: readonly string[];
  invoke?: (channel: string, payload?: unknown) => Promise<unknown>;
}

@Injectable({ providedIn: 'root' })
export class NativeStatusService {

  private readonly platform = inject(PlatformService);
  private readonly appVersion = inject(AppVersionService);

  /**
   * Set once the app has actually painted. A signal, not a field, because the push
   * below is an `effect()` and this is the value it exists to react to.
   */
  private readonly interactive = signal(false);

  /** False on the web, and after a shell rejects the channel. */
  private sending = false;

  constructor() {
    if (!this.resolve()) {
      return;
    }
    this.sending = true;

    // `afterNextRender`, not the constructor: a root service is constructed during
    // bootstrap, before anything is on screen, so reporting `interactive` here would
    // tell the shell to drop its splash onto a page that has not painted yet.
    afterNextRender(() => this.interactive.set(true));

    // One push per state change, and one immediately for the initial state.
    effect(() => void this.push(this.snapshot()));
  }

  /**
   * The current state, exactly as sent. Public so specs can assert on it without
   * having to observe IPC.
   */
  snapshot(): NativeAppStatus {
    return {
      interactive: this.interactive(),
      swEnabled: this.appVersion.isEnabled,
      downloading: this.appVersion.downloading(),
      updateReady: this.appVersion.ready(),
    };
  }

  /**
   * Send one snapshot. Never throws and never rejects: this is telemetry for a splash
   * screen, and nothing in the app should care whether it arrived.
   */
  private async push(status: NativeAppStatus): Promise<void> {
    if (!this.sending) {
      return;
    }
    const native = this.resolve();
    if (!native?.invoke) {
      return;
    }
    try {
      await native.invoke(CHANNEL, status);
    } catch {
      // A shell older than the channel rejects it. Stop, so a page that changes state
      // often does not produce one rejected promise per change for the rest of the
      // session.
      this.sending = false;
    }
  }

  /** The shell bridge, if this app is running in a shell that supports the channel. */
  private resolve(): CrewdleNative | null {
    if (!this.platform.is('electron')) {
      return null;
    }
    const native = (window as unknown as { crewdleNative?: CrewdleNative }).crewdleNative;
    if (typeof native?.invoke !== 'function') {
      return null;
    }
    // When the shell advertises its channels, trust the list. When it does not, try
    // anyway and let `push()` disable itself on the rejection.
    if (native.channels && !native.channels.includes(CHANNEL)) {
      return null;
    }
    return native;
  }
}
